export const DRAFT_NAMESPACE = 'hms:draft:v1:';

const SCHEMA_VERSION = 1;
const TTL_MS = 8 * 60 * 60 * 1000;
const MAX_DRAFT_BYTES = 256 * 1024;
const PURGE_WRITE_SUPPRESSION_MS = 30 * 1000;
const REQUIRED_IDENTITY_FIELDS = ['hospitalId', 'branchId', 'userId', 'workflow', 'patientId', 'visitId', 'recordId'];
const WRITER_ID = typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const hasStorage = () => typeof window !== 'undefined' && Boolean(window.localStorage);
const normalizeIdentityPart = (value) => encodeURIComponent(String(value).trim());
let writesSuppressedUntil = 0;

export const areDraftWritesSuppressed = () => Date.now() < writesSuppressedUntil;

const suppressDraftWritesForPurge = () => {
    writesSuppressedUntil = Date.now() + PURGE_WRITE_SUPPRESSION_MS;
};

export const buildDraftKey = (identity) => {
    if (!identity || REQUIRED_IDENTITY_FIELDS.some((field) => identity[field] === null || identity[field] === undefined || String(identity[field]).trim() === '')) {
        return null;
    }
    return `${DRAFT_NAMESPACE}${REQUIRED_IDENTITY_FIELDS.map((field) => normalizeIdentityPart(identity[field])).join(':')}`;
};

const matchesIdentity = (draft, identity) => REQUIRED_IDENTITY_FIELDS.every(
    (field) => String(draft[field]) === String(identity[field]),
);

const isValidEnvelope = (draft, identity, formVersion) => Boolean(
    draft
    && draft.schemaVersion === SCHEMA_VERSION
    && draft.formVersion === formVersion
    && draft.values
    && typeof draft.values === 'object'
    && Number.isFinite(Date.parse(draft.savedAt))
    && Number.isFinite(Date.parse(draft.expiresAt))
    && matchesIdentity(draft, identity),
);

export const removeDraft = (key) => {
    if (!key || !hasStorage()) return;
    try {
        window.localStorage.removeItem(key);
    } catch {
        // Draft cleanup must never interrupt the clinical workflow.
    }
};

export const readDraft = ({ key, identity, formVersion }) => {
    if (!key || !hasStorage()) return null;
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const draft = JSON.parse(raw);
        if (!isValidEnvelope(draft, identity, formVersion) || Date.parse(draft.expiresAt) <= Date.now()) {
            removeDraft(key);
            return null;
        }
        return draft;
    } catch {
        removeDraft(key);
        return null;
    }
};

export const writeDraft = ({ key, identity, formVersion, writerId, values, sourceUpdatedAt, sourceFingerprint }) => {
    if (areDraftWritesSuppressed()) return { ok: false, error: 'suppressed' };
    if (!key || !hasStorage()) return { ok: false, error: 'unavailable' };
    const savedAt = new Date();
    const envelope = {
        schemaVersion: SCHEMA_VERSION,
        formVersion,
        ...identity,
        sourceUpdatedAt: sourceUpdatedAt || null,
        sourceFingerprint: sourceFingerprint || null,
        savedAt: savedAt.toISOString(),
        expiresAt: new Date(savedAt.getTime() + TTL_MS).toISOString(),
        writerId,
        values,
    };
    try {
        const serialized = JSON.stringify(envelope);
        if (new Blob([serialized]).size > MAX_DRAFT_BYTES) return { ok: false, error: 'too-large' };
        window.localStorage.setItem(key, serialized);
        return { ok: true, draft: envelope };
    } catch (error) {
        return { ok: false, error };
    }
};

export const cleanupDraftNamespace = () => {
    suppressDraftWritesForPurge();
    if (!hasStorage()) return;
    try {
        const keys = [];
        for (let index = 0; index < window.localStorage.length; index += 1) {
            const key = window.localStorage.key(index);
            if (key?.startsWith(DRAFT_NAMESPACE)) keys.push(key);
        }
        keys.forEach((key) => window.localStorage.removeItem(key));
    } catch {
        // Logout must continue even when browser storage is unavailable.
    }
};

export const getDraftWriterId = () => WRITER_ID;
